// src/pages/organization/ExamPreviewPage.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  useParams,
  useSearchParams,
  Link,
  useNavigate,
} from "react-router-dom";
import { useTranslation } from "react-i18next";
import { httpRequest } from "../../utils/httpRequest";
import {
  ChevronLeft,
  ChevronRight,
  Bookmark,
  X,
  Play,
  Pause,
  Volume2,
  VolumeX,
  Volume1,
  Clock,
} from "lucide-react";
import { toast } from "react-toastify";
import ItemRenderer from "../../components/exam-builder/ItemRenderer";
import AudioPlayer from "../../components/exam-builder/AudioPlayer";
import PartNav from "../../components/exam-player/PartNav";
import PartNavSmart from "../../components/exam-player/PartNavSmart";
import { ReviewModal } from "../../components/exam-builder/ReviewModal";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useUserStore } from "../../stores/useUserStore";
import AiSectionRunner from "../../components/exam-player/AiSectionRunner";

function hasProAiAccess(subscriptions, categoryId) {
  console.log(subscriptions);
  if (!Array.isArray(subscriptions) || !categoryId) return false;

  const now = Date.now();
  const catId = String(categoryId);

  return subscriptions.some((s) => {
    const planOk = String(s?.plan || "").toLowerCase() === "pro";
    const sameCat = String(s?.category?._id || "") === catId;

    const startOk = s?.startAt ? new Date(s.startAt).getTime() <= now : true;
    const endOk = s?.endAt ? new Date(s.endAt).getTime() >= now : false;

    return planOk && sameCat && startOk && endOk;
  });
}

const draftKey = (examId) => `attemptDraft:${examId}`;
function readDraft(examId) {
  try {
    const raw = localStorage.getItem(draftKey(examId));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}
function writeDraft(examId, data) {
  try {
    localStorage.setItem(draftKey(examId), JSON.stringify(data));
  } catch {}
}
function clearDraft(examId) {
  try {
    localStorage.removeItem(draftKey(examId));
  } catch {}
}
const findPartById = (section, pid) => {
  if (!pid) return null;
  return (section?.parts || []).find((p) => p.id === pid) || null;
};

const formatMMSS = (sec) => {
  if (sec == null || Number.isNaN(sec)) return "--:--";
  const s = Math.max(0, Math.floor(sec));
  const m = Math.floor(s / 60)
    .toString()
    .padStart(2, "0");
  const r = (s % 60).toString().padStart(2, "0");
  return `${m}:${r}`;
};

export default function ExamPreviewPage() {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const [sp] = useSearchParams();
  const mode = sp.get("mode") || "device";
  const isPractice = mode === "practice";
  const startSectionKey = sp.get("section") || null;

  const [loading, setLoading] = useState(true);
  const [exam, setExam] = useState(null);
  const [answers, setAnswers] = useState({});
  const [timeLeft, setTimeLeft] = useState(null);
  const [timerActive, setTimerActive] = useState(false);
  const [autoPlaySignal, setAutoPlaySignal] = useState(0);
  const [playArmed, setPlayArmed] = useState(false);
  const [startedAt, setStartedAt] = useState(null);
  const [finishedAt, setFinishedAt] = useState(null);
  const [bookmarks, setBookmarks] = useState(() => new Set()); // pageIdx های بوکمارک‌شده
  const [reviewOpen, setReviewOpen] = useState(false);
  const [attemptRef, setAttemptRef] = useState(() => {
    if (isPractice) return null; // ✅ در practice همیشه جدید
    const saved = localStorage.getItem(`attempt:${id}`);
    return saved ? JSON.parse(saved) : null;
  });
  const [pageIdx, setPageIdx] = useState(0);
  const [aiHelp, setAiHelp] = useState({
    open: false,
    loading: false,
    text: "",
    rendered: "", // برای تایپ انیمیشنی
    key: null,
    error: null,
  });

  const { subscriptions, fetchSubscriptions } = useUserStore();

  const navigate = useNavigate();
  const prevLangRef = useRef(null);

  // ردیابی زمان هر سؤال در حالت one_per_page
  // ساختار: times[si][qi] = ثانیه
  const [questionTimes, setQuestionTimes] = useState({});
  const [viewKey, setViewKey] = useState(null);

  // صفحات: intro آزمون → intro سکشن → (all_at_once یا آیتم‌ها)
  const pages = useMemo(() => {
    if (!exam) return [];
    const result = [{ type: "examIntro" }];

    (exam.sections || []).forEach((s, si) => {
      // صفحه‌ی شروع سکشن
      result.push({ type: "sectionIntro", si });

      if (s?.type === "ai") {
        result.push({ type: "aiSectionRun", si });
        return;
      }

      // گروه‌بندی آیتم‌ها بر اساس anchorId
      const items = s.items || [];
      const byPart = new Map();
      items.forEach((it, idx) => {
        const pid = it.anchorId || ""; // گروهِ بدون پارت
        if (!byPart.has(pid)) byPart.set(pid, []);
        byPart.get(pid).push({ it, qi: idx });
      });

      // لیست پارت‌ها (برای حفظ ترتیب UI)
      const parts = Array.isArray(s.parts) ? s.parts : [];
      const partOrderIds = [...parts.map((p) => p.id)];
      // اگر آیتم بدون پارت داریم، در انتها گروه default را هم اضافه کن
      if (byPart.has("") && !partOrderIds.includes("")) partOrderIds.push("");

      const mode = s.displayMode || "one_per_page";

      partOrderIds.forEach((pid) => {
        const group = byPart.get(pid) || [];
        if (!group.length) return;

        if (mode === "all_at_once") {
          // یک صفحه برای کل پارت
          result.push({
            type: "partAllAtOnce",
            si,
            pid,
            qis: group.map((g) => g.qi),
          });
        } else {
          // برای هر آیتم یک صفحه، اما promptHtml پارت در بالا نشان داده می‌شود
          group.forEach((g) => {
            result.push({ type: "partOnePerPage", si, pid, qi: g.qi });
          });
        }
      });
    });

    return result;
  }, [exam]);

  useEffect(() => {
    // اگر جای دیگه این کار انجام نشده
    fetchSubscriptions?.();
  }, [fetchSubscriptions]);

  const canUseAi = useMemo(() => {
    return hasProAiAccess(subscriptions, exam?.category?._id);
  }, [subscriptions, exam?.category?._id]);

  console.log("can Use AI", canUseAi);

  useEffect(() => {
    if (!exam) return;
    if (isPractice) return; // ✅ practice ادامه‌ی قبلی نیار

    const draft = readDraft(id);
    if (draft?.answers) setAnswers((prev) => ({ ...prev, ...draft.answers }));
    if (draft?.pageIdx != null) setPageIdx(draft.pageIdx);
    if (draft?.startedAt) setStartedAt(draft.startedAt);
  }, [exam, id, isPractice]);

  useEffect(() => {
    if (!exam) return;
    if (isPractice) return; // ✅ practice چیزی ذخیره نکن

    writeDraft(id, {
      examId: id,
      answers,
      pageIdx,
      startedAt,
      updatedAt: new Date().toISOString(),
    });
  }, [answers, pageIdx, startedAt, exam, id, isPractice]);

  useEffect(() => {
    if (!exam) return;
    if (isPractice) return; // ✅ practice چیزی ذخیره نکن

    writeDraft(id, {
      examId: id,
      answers,
      pageIdx,
      startedAt,
      updatedAt: new Date().toISOString(),
    });
  }, [answers, pageIdx, startedAt, exam, id, isPractice]);

  useEffect(() => {
    // فقط یکبار، زبان اولیه سیستم رو ذخیره کن
    if (prevLangRef.current == null) {
      prevLangRef.current = i18n.language;
    }

    // وقتی از صفحه خارج شدیم، زبان رو برگردون
    return () => {
      const prev = prevLangRef.current;
      if (prev && i18n.language !== prev) {
        i18n.changeLanguage(prev);
      }
    };
  }, [i18n]);

  useEffect(() => {
    if (!aiHelp.open) return;
    if (aiHelp.loading) return;
    if (!aiHelp.text) return;

    let i = 0;
    setAiHelp((p) => ({ ...p, rendered: "" }));

    const iv = setInterval(() => {
      i += 8; // سرعت تایپ (هر بار چند کاراکتر)
      setAiHelp((p) => ({ ...p, rendered: aiHelp.text.slice(0, i) }));
      if (i >= aiHelp.text.length) clearInterval(iv);
    }, 15);

    return () => clearInterval(iv);
  }, [aiHelp.open, aiHelp.loading, aiHelp.text]);

  useEffect(() => {
    const beforeUnload = (e) => {
      // اگر شروع کرده و هنوز نتیجه نهایی نشده
      const hasStarted = !!startedAt;
      const notFinished = !finishedAt;
      if (hasStarted && notFinished) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", beforeUnload);
    return () => window.removeEventListener("beforeunload", beforeUnload);
  }, [startedAt, finishedAt]);

  const totalQuestions = useMemo(() => {
    if (!exam) return 0;
    let sum = 0;
    (exam.sections || []).forEach((s) => {
      (s.items || []).forEach((row) => {
        if (row?.item?.kind !== "audio_gate") sum += 1;
      });
    });
    return sum;
  }, [exam]);

  const curr = pages[pageIdx] || null;
  const sectionItemPageIdxs = useMemo(() => {
    if (!curr || curr.si == null) return [];
    const si = curr.si;
    const arr = [];
    pages.forEach((pg, idx) => {
      if (pg.si === si && pg.type === "partOnePerPage") {
        const row = exam?.sections?.[si]?.items?.[pg.qi];
        if (row?.item?.kind !== "audio_gate") arr.push(idx);
      }
    });
    return arr;
  }, [pages, curr, exam]);

  const restoreLang = async () => {
    const prev = prevLangRef.current;
    if (prev && i18n.language !== prev) {
      await i18n.changeLanguage(prev);
    }
  };

  // همهٔ صفحات one-per-page (سؤال + گِیت)
  const sectionAllPageIdxs = useMemo(() => {
    if (!curr || curr.si == null) return [];
    const si = curr.si;
    const arr = [];
    pages.forEach((pg, idx) => {
      if (pg.si === si && pg.type === "partOnePerPage") arr.push(idx);
    });
    return arr;
  }, [pages, curr]);

  // پوزیشن فعلی در بین «فقط سؤال‌ها» (برای اینکه دکمه‌های هدر فقط روی سؤال ظاهر شوند)
  const localPos = useMemo(() => {
    if (!sectionItemPageIdxs.length) return -1;
    return sectionItemPageIdxs.indexOf(pageIdx);
  }, [sectionItemPageIdxs, pageIdx]);

  // جایگاه بین همهٔ صفحات (برای Next)
  const localPosAny = useMemo(() => {
    if (!sectionAllPageIdxs.length) return -1;
    return sectionAllPageIdxs.indexOf(pageIdx);
  }, [sectionAllPageIdxs, pageIdx]);

  const prevIsGate = useMemo(() => {
    if (localPosAny <= 0) return false;
    const prevIdx = sectionAllPageIdxs[localPosAny - 1];
    const prevPg = pages[prevIdx];
    if (!prevPg || prevPg.type !== "partOnePerPage") return false;
    const si = curr?.si;
    const row = exam?.sections?.[si]?.items?.[prevPg.qi];
    return row?.item?.kind === "audio_gate";
  }, [localPosAny, sectionAllPageIdxs, pages, curr, exam]);

  const canPrevInSection = /* قبلی: localPos > 0 */ localPos > 0 && !prevIsGate;

  const openAiHelp = async ({ item, si, qi }) => {
    try {
      if (!canUseAi) {
        return toast.error(
          "برای استفاده از قابلیت های هوش مصنوعی باید اشتراک خود را به پلن پرو ارتقاع دهید",
        );
      }
      setAiHelp({
        open: true,
        loading: true,
        text: "",
        rendered: "",
        key: `${si}-${qi}-${item?._id}`,
        error: null,
      });

      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const userAnswer = answers?.[si]?.[qi] ?? null;

      const res = await httpRequest(
        "POST",
        "/ai/explain-item",
        {
          itemId: item._id,
          userAnswer,
          lang: i18n.language,
        },
        { "x-access-token": user.token },
      );

      const md = res?.data?.markdown || "";
      setAiHelp((p) => ({ ...p, loading: false, text: md }));
    } catch (e) {
      setAiHelp((p) => ({
        ...p,
        loading: false,
        error:
          e.response.status === 429
            ? "شما برای امروز به محدودیت استفاده از آپشن های هوش مصنوعی رسیده اید برای استفاده بیشتر فردا برگردید"
            : e?.message || "AI help failed",
      }));
    }
  };

  const goPrevInSection = () => {
    if (!canPrevInSection) return;
    // حرکت فقط بین سوال‌ها (gate ها را نادیده می‌گیریم)
    setPageIdx(sectionItemPageIdxs[localPos - 1]);
  };

  // Next روی همهٔ صفحات (سؤال + گِیت)
  const canNextInSection =
    localPosAny >= 0 && localPosAny < sectionAllPageIdxs.length - 1;
  const goNextInSection = () => {
    if (!canNextInSection) return;
    setPageIdx(sectionAllPageIdxs[localPosAny + 1]); // ← اگر صفحهٔ بعد گِیت باشه، وارد گِیت می‌شه
  };
  const getQuestionNumber = (si, qi) => {
    const items = exam?.sections?.[si]?.items || [];
    let n = 0;
    for (let i = 0; i <= qi; i++) {
      if (items[i]?.item?.kind !== "audio_gate") n++;
    }
    return n;
  };

  // پخش‌کننده سفارشی
  const audioRef = useRef(null);
  const [audioState, setAudioState] = useState({
    url: "",
    allowPause: true,
    maxPlays: undefined,
    playCount: 0,
    isPlaying: false,
    duration: 0,
    currentTime: 0,
    volume: 1,
  });

  // تایمر آزمون/سکشن
  // پیدا کردن این خط در کد خودتان و اصلاح آن
  const [examTimer, setExamTimer] = useState({
    totalSec: 0,
    leftSec: 0,
    running: false,
  });

  // اضافه کردن یک useEffect برای مقداردهی اولیه وقتی دیتا لود می‌شود
  useEffect(() => {
    if (exam?.timeMode === "global" && exam?.totalTime) {
      const totalSeconds = exam.totalTime * 60;
      setExamTimer({
        totalSec: totalSeconds,
        leftSec: totalSeconds,
        running: false, // تا دکمه شروع زده نشود، اجرا نمی‌شود
      });
    }
  }, [exam]);

  const [sectionTimer, setSectionTimer] = useState({
    totalSec: undefined,
    leftSec: undefined,
    running: false,
  });

  const hasExamTimer =
    typeof examTimer?.totalSec === "number" && examTimer.totalSec > 0;

  const hasSectionTimer =
    typeof sectionTimer?.totalSec === "number" && sectionTimer.totalSec > 0;

  // برای تایمر قدیمی (timeLeft/timerActive) هم بهتره امنش کنیم
  const hasLegacySectionTimer =
    timerActive && typeof timeLeft === "number" && timeLeft >= 0;

  const setItemAnswers = (si, qi, map) => {
    setAnswers((prev) => {
      const sec = prev[si] ? { ...prev[si] } : {};
      sec[qi] = map;
      return { ...prev, [si]: sec };
    });
  };

  // آیا q1 (۱-بیس) داخل بازه‌ی بلاک است؟
  const inRange = (b, q1) => {
    const f = typeof b?.from === "number" ? b.from : -Infinity;
    const t = typeof b?.to === "number" ? b.to : Infinity;
    return q1 >= f && q1 <= t;
  };

  // ✅ نسخه‌ی جدید: گروه‌بندی به ترتیب طبیعی سوال‌ها
  const buildGroupedPart = (part, sec, qis) => {
    const blocks = Array.isArray(part?.blocks) ? part.blocks : [];

    // بلاک‌های «عمومی» (بدون from/to) فقط برای نمایش ثابت یک‌جایی (مثل ستون چپ)
    const publicBlocks = blocks.filter(
      (b) => typeof b?.from !== "number" && typeof b?.to !== "number",
    );

    // بلاک‌های بازه‌دار
    const rangedBlocks = blocks
      .filter((b) => typeof b?.from === "number" || typeof b?.to === "number")
      .sort((a, b) => (a.from ?? -Infinity) - (b.from ?? -Infinity)); // برای پیدا کردن سریع‌تر

    const findBlockFor = (q1) => rangedBlocks.find((b) => inRange(b, q1));

    const sections = [];
    const processed = new Set();

    // qis = ایندکس‌های 0-b سوال‌های همین پارت به ترتیب
    for (let i = 0; i < (qis || []).length; i++) {
      const qi = qis[i];
      if (processed.has(qi)) continue;

      const q1 = qi + 1;
      const host = findBlockFor(q1);

      if (host) {
        // همه‌ی سوال‌های داخل بازه‌ی همین بلاک (به ترتیبی که در qis ظاهر می‌شن)
        const blockQis = [];
        for (let j = i; j < (qis || []).length; j++) {
          const qj = qis[j];
          const qj1 = qj + 1;
          if (inRange(host, qj1)) {
            blockQis.push(qj);
            processed.add(qj);
          }
        }
        sections.push({ kind: "block", block: host, qis: blockQis });
        // توجه: سوال‌های خارج از رنج، بعداً در حلقه به‌عنوان single اضافه می‌شن
      } else {
        sections.push({ kind: "single", qi });
        processed.add(qi);
      }
    }

    return { sections, publicBlocks };
  };

  const resolveLangForPage = (exam, curr) => {
    if (!exam) return null;
    const section = curr?.si != null ? exam.sections?.[curr.si] : null;

    // اگر خواستی سکشن هم سیاست داشته باشد، این خط اولویت می‌دهد
    const mode =
      section?.meta?.languageMode ?? exam?.meta?.languageMode ?? "auto";

    if (mode === "en_only") return "en";
    if (mode === "fa_only") return "fa";
    // bilingual یا auto → تغییر نده
    return null;
  };

  const buildSectionPartNav = (sectionIndex) => {
    const s = exam?.sections?.[sectionIndex];
    if (!s) return { parts: [], mode: "one_per_page" };
    const mode = s.displayMode || "one_per_page";

    // آیتم‌ها بر اساس anchorId
    const buckets = new Map(); // pid -> { startQi, endQi, count }
    (s.items || []).forEach((row, qi) => {
      const pid = row.anchorId || "";
      if (!buckets.has(pid))
        buckets.set(pid, { startQi: qi + 1, endQi: qi + 1, count: 0 });
      const b = buckets.get(pid);
      b.endQi = qi + 1;
      b.count += 1;
    });

    // ترتیب پارت‌ها طبق s.parts؛ بدون پارت در انتها
    const order = [...(s.parts || []).map((p) => p.id)];
    if (buckets.has("") && !order.includes("")) order.push("");

    // اولین ایندکس صفحه برای هر پارت
    const firstIndexByPid = new Map();
    pages.forEach((pg, idx) => {
      if (
        pg.si === sectionIndex &&
        (pg.type === "partAllAtOnce" || pg.type === "partOnePerPage")
      ) {
        const pid = pg.pid || "";
        if (!firstIndexByPid.has(pid)) firstIndexByPid.set(pid, idx);
      }
    });

    const parts = order
      .filter((pid) => buckets.has(pid))
      .map((pid) => {
        const meta = buckets.get(pid);
        return {
          pid,
          label: pid || "No Part",
          count: meta.count,
          startQi: meta.startQi,
          endQi: meta.endQi,
          firstPageIdx: firstIndexByPid.get(pid ?? "") ?? null,
        };
      });

    return { parts, mode };
  };

  const getActivePartIndex = (si) => {
    const { parts } = buildSectionPartNav(si);
    if (!parts.length) return -1;
    const pid =
      curr?.si === si &&
      (curr.type === "partAllAtOnce" || curr.type === "partOnePerPage")
        ? curr.pid || ""
        : parts[0].pid;
    return Math.max(
      0,
      parts.findIndex((p) => p.pid === pid),
    );
  };

  // پرش به پارت با ایندکس
  const goToPartIndex = (si, idx) => {
    const { parts } = buildSectionPartNav(si);
    const target = parts[idx];
    if (target && typeof target.firstPageIdx === "number") {
      setPageIdx(target.firstPageIdx);
    }
  };

  const hydrateItemsIfNeeded = async (ex, token) => {
    // اگر آیتم‌ها پاپیوله نیستن، بیار
    const missing = [];
    (ex.sections || []).forEach((s) =>
      (s.items || []).forEach((it) => {
        const v = it?.item;
        if (typeof v === "string" || (v && !v.kind)) {
          missing.push(typeof v === "string" ? v : String(v));
        }
      }),
    );
    const ids = [...new Set(missing)];
    if (!ids.length) return ex;

    const qs = encodeURIComponent(ids.join(","));
    const res = await httpRequest(
      "GET",
      `/item?ids=${qs}`,
      {},
      { "x-access-token": token },
    );
    const list = res?.data?.data || res?.data || [];
    const map = new Map(list.map((it) => [String(it._id), it]));

    const cloned = JSON.parse(JSON.stringify(ex));
    (cloned.sections || []).forEach((s) => {
      s.items = (s.items || []).map((it) => {
        const v = it?.item;
        if (typeof v === "string")
          return { ...it, item: map.get(v) || it.item };
        if (v && !v.kind && v._id)
          return { ...it, item: map.get(String(v._id)) || it.item };
        return it;
      });
    });
    return cloned;
  };

  const finishAiSection = async (si, aiResult) => {
    const user = JSON.parse(localStorage.getItem("user") || "{}");

    const body = {
      exam: exam._id,
      sectionIndex: si,
      aiResult, // { turns: [...], summary, score, ... }
      startedAt: startedAt || new Date().toISOString(),
      finishedAt:
        si === exam.sections.length - 1 ? new Date().toISOString() : undefined,
    };

    if (attemptRef?.attemptId) body.attemptId = attemptRef.attemptId;
    else if (attemptRef?.attemptKey) body.attemptKey = attemptRef.attemptKey;

    const res = await httpRequest("POST", "/attempt/section-ai", body, {
      "x-access-token": user.token,
    });

    const data = res?.data?.data || res?.data;
    if (!attemptRef?.attemptId && data?.attemptId) {
      const ref = { attemptId: data.attemptId, attemptKey: data.attemptKey };
      setAttemptRef(ref);
      localStorage.setItem(`attempt:${id}`, JSON.stringify(ref));
    }

    const submitAttemptId = data?.attemptId || attemptRef?.attemptId;

    if (si === exam.sections.length - 1) {
      await httpRequest(
        "POST",
        "/attempt/submit",
        { attemptId: submitAttemptId },
        {
          "x-access-token": user.token,
        },
      );
      clearDraft(id);
      localStorage.removeItem(`attempt:${id}`);
      await restoreLang();
      toast.success(t("preview.exam_finished") || "آزمون تمام شد");
      navigate(`/organization/exam-builder`);
      return;
    }

    const nextSectionIntroIdx = pages.findIndex(
      (p, idx) => idx > pageIdx && p.type === "sectionIntro" && p.si === si + 1,
    );
    setPageIdx(nextSectionIntroIdx >= 0 ? nextSectionIntroIdx : pageIdx + 1);
  };

  const finishSection = async () => {
    try {
      if (!exam || curr?.si == null) return;
      const si = curr.si;
      const section = exam.sections[si];
      const secAnswers = answers[si] || {}; // { qi -> value }
      const secTimes = questionTimes[si] || {};

      // تبدیل به responses
      const responses = (section.items || []).map((row, qi) => {
        const itemDoc = row?.item;
        const itemId = itemDoc?._id;
        const value = secAnswers[qi] ?? null;

        const attachments = [];
        let needsReview = false;

        // ✅ انواعی که باید دستی تصحیح شوند
        if (
          itemDoc?.kind === "essay" ||
          itemDoc?.kind === "audio_prompt" ||
          itemDoc?.kind === "speaking_integrated" ||
          itemDoc?.kind === "writing_prompt" ||
          itemDoc?.kind === "file_upload"
        ) {
          needsReview = true;
        }

        // ✅ اگر ضبط صدا داری و URL سروری وجود دارد، بعنوان attachment بفرست
        const recUrl = value?.recording?.url;
        if (recUrl && !recUrl.startsWith("blob:")) attachments.push(recUrl);

        return {
          item: itemId,
          sectionIndex: si,
          order: qi + 1,
          value,
          timeSec: Number(secTimes[qi]) || 0,
          needsReview,
          attachments,
        };
      });

      const user = JSON.parse(localStorage.getItem("user") || "{}");

      const body = {
        exam: exam._id,
        sectionIndex: si,
        responses,
        startedAt: startedAt || new Date().toISOString(),
        finishedAt:
          si === exam.sections.length - 1
            ? new Date().toISOString()
            : undefined,
      };

      if (attemptRef?.attemptId) body.attemptId = attemptRef.attemptId;
      else if (attemptRef?.attemptKey) body.attemptKey = attemptRef.attemptKey;

      const res = await httpRequest("POST", "/attempt/section", body, {
        "x-access-token": user.token,
      });
      const data = res?.data?.data || res?.data;

      if (!attemptRef?.attemptId && data?.attemptId) {
        const ref = { attemptId: data.attemptId, attemptKey: data.attemptKey };
        setAttemptRef(ref);
        localStorage.setItem(`attempt:${id}`, JSON.stringify(ref));
      }

      const submitAttemptId = data?.attemptId || attemptRef?.attemptId;

      // اگر سکشن آخر بود، submit
      if (si === exam.sections.length - 1) {
        await httpRequest(
          "POST",
          "/attempt/submit",
          { attemptId: submitAttemptId },
          { "x-access-token": user.token },
        );
        clearDraft(id);
        localStorage.removeItem(`attempt:${id}`);
        await restoreLang();
        toast.success(t("preview.exam_finished") || "آزمون تمام شد");
        navigate(`/organization/exam-builder`);
        return;
      }

      // برو سکشن بعد
      const nextSectionIntroIdx = pages.findIndex(
        (p, idx) =>
          idx > pageIdx && p.type === "sectionIntro" && p.si === si + 1,
      );
      setPageIdx(nextSectionIntroIdx >= 0 ? nextSectionIntroIdx : pageIdx + 1);
      setTimerActive(false);
      setTimeLeft(null);
    } catch (err) {
      toast.error(err?.response?.data?.message || t("common.unknown_error"));
    }
  };

  useEffect(() => {
    const target = resolveLangForPage(exam, curr);
    if (target && i18n.language !== target) {
      i18n.changeLanguage(target);
    }
  }, [exam, curr, i18n]);

  // دریافت آزمون
  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    (async () => {
      setLoading(true);
      try {
        const res = await httpRequest(
          "GET",
          `/exam/${id}`,
          {},
          { "x-access-token": user.token },
        );
        const data = res?.data?.data || res?.data;
        if (!data?._id) throw new Error(res?.message || "NOT_FOUND");

        const hydrated = await hydrateItemsIfNeeded(data, user.token);
        const totalExamSec =
          typeof hydrated?.totalTimeMin === "number" &&
          hydrated.totalTimeMin > 0
            ? hydrated.totalTimeMin * 60
            : undefined;

        setExam(hydrated);
        if (isPractice && startSectionKey) {
          const targetSectionIndex = hydrated.sections.findIndex(
            (s) => s.key === startSectionKey,
          );

          if (targetSectionIndex >= 0) {
            // صفحه‌ی intro سکشن مربوطه رو پیدا کن
            const introIdx = pages.findIndex(
              (p) => p.type === "sectionIntro" && p.si === targetSectionIndex,
            );
            if (introIdx >= 0) {
              setPageIdx(introIdx);
            }
          }
        }
        setExamTimer({
          totalSec: totalExamSec,
          leftSec: totalExamSec,
          running: false,
        });
      } catch (err) {
        toast.error(err?.message || t("common.unknown_error"));
      } finally {
        setLoading(false);
      }
    })();
  }, [id, t]);

  // تیک تایمر هر ثانیه

  useEffect(() => {
    if (!exam || !mode) return;
    if (!isPractice || !startSectionKey) return;

    const targetSectionIndex = exam.sections.findIndex(
      (s) => s.key === startSectionKey,
    );

    if (targetSectionIndex >= 0) {
      const introIdx = pages.findIndex(
        (p) => p.type === "sectionIntro" && p.si === targetSectionIndex,
      );
      if (introIdx >= 0) {
        setPageIdx(introIdx);
      }
    }
  }, [exam, mode, startSectionKey, pages]);

  // Sync صوت/تایمر سکشن با تغییر صفحه
  useEffect(() => {
    if (!exam || !curr) return;
    const si = curr.si;
    if (si == null) {
      // بیرون سکشن
      setAudioState((s) => ({
        ...s,
        url: "",
        isPlaying: false,
        currentTime: 0,
        duration: 0,
      }));
      return;
    }
    const section = exam.sections?.[si];
    const af = section?.audioFile;
    const url = af?.url || "";

    if (section?.type === "ai") {
      setAudioState((s) => ({
        ...s,
        url: "",
        isPlaying: false,
        currentTime: 0,
        duration: 0,
      }));
      const secTotal =
        typeof section?.timeLimitSec === "number" && section.timeLimitSec > 0
          ? section.timeLimitSec
          : undefined;
      setSectionTimer({
        totalSec: secTotal,
        leftSec: secTotal,
        running: false,
      });
      return;
    }

    setAudioState({
      url,
      allowPause: !!af?.allowPause,
      maxPlays: typeof af?.maxPlays === "number" ? af.maxPlays : undefined,
      playCount: 0,
      isPlaying: false,
      duration: 0,
      currentTime: 0,
      volume: 1,
    });
    const secTotal =
      typeof section?.timeLimitSec === "number" && section.timeLimitSec > 0
        ? section.timeLimitSec
        : undefined;
    setSectionTimer({ totalSec: secTotal, leftSec: secTotal, running: false });

    // فقط اگر از intro گذشتیم و آرم فعال است و صوت موجود است
    if (playArmed && si != null && audioSrc) {
      setAutoPlaySignal((s) => s + 1); // سیگنال پخش
      setPlayArmed(false); // آرم را خنثی کن
    }
  }, [pageIdx, exam]);

  // کنترل‌های سفارشی صوت
  // رویدادها را به <audio> نامرئی متصل می‌کنیم
  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;
    const onLoaded = () =>
      setAudioState((s) => ({ ...s, duration: el.duration || 0 }));
    const onTime = () =>
      setAudioState((s) => ({ ...s, currentTime: el.currentTime || 0 }));
    const onPlay = () =>
      setAudioState((s) => {
        // شمارش دفعات play
        const nextCount = s.isPlaying
          ? s.playCount
          : Math.min((s.playCount || 0) + 1, s.maxPlays || Infinity);
        return { ...s, isPlaying: true, playCount: nextCount };
      });
    const onPause = () =>
      setAudioState((s) => {
        // اگر اجازه مکث نداریم، ادامه بده
        if (!s.allowPause && el) {
          el.play().catch(() => {});
          return { ...s, isPlaying: true };
        }
        return { ...s, isPlaying: false };
      });
    const onEnded = () => setAudioState((s) => ({ ...s, isPlaying: false }));

    el.addEventListener("loadedmetadata", onLoaded);
    el.addEventListener("timeupdate", onTime);
    el.addEventListener("play", onPlay);
    el.addEventListener("pause", onPause);
    el.addEventListener("ended", onEnded);
    return () => {
      el.removeEventListener("loadedmetadata", onLoaded);
      el.removeEventListener("timeupdate", onTime);
      el.removeEventListener("play", onPlay);
      el.removeEventListener("pause", onPause);
      el.removeEventListener("ended", onEnded);
    };
  }, [audioRef]);

  useEffect(() => {
    const iv = setInterval(() => {
      // ۱. مدیریت تایمر گلوبال
      if (exam?.timeMode === "global") {
        setExamTimer((prev) => {
          if (!prev.running || prev.leftSec <= 0) return prev;
          const newLeft = prev.leftSec - 1;
          if (newLeft === 0) {
            toast.error("زمان آزمون به پایان رسید");
            // اینجا می‌توانید تابع ثبت نهایی را صدا بزنید
          }
          return { ...prev, leftSec: newLeft };
        });
      }

      // ۲. مدیریت تایمر سکشن (کد قبلی شما)
      if (timerActive && timeLeft != null && timeLeft > 0) {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            setTimerActive(false);
            // finishSection(); // اگر می‌خواهید خودکار رد شود
            return 0;
          }
          return prev - 1;
        });
      }
    }, 1000);

    return () => clearInterval(iv);
  }, [timerActive, timeLeft, exam?.timeMode]);

  useEffect(() => {
    const onKey = (e) => {
      if (!isOnSection) return;
      const { parts } = buildSectionPartNav(curr.si);
      if (parts.length < 2) return;
      const activeIdx = getActivePartIndex(curr.si);
      if (e.key === "ArrowRight") {
        goToPartIndex(curr.si, Math.min(parts.length - 1, activeIdx + 1));
      } else if (e.key === "ArrowLeft") {
        goToPartIndex(curr.si, Math.max(0, activeIdx - 1));
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [curr, exam, pages]);

  useEffect(() => {
    if (!curr) return;
    // فقط one_per_page که سوال تکی نشان می‌دهیم
    if (curr.type === "partOnePerPage") {
      setViewKey({ si: curr.si, qi: curr.qi });
    } else {
      setViewKey(null);
    }
  }, [curr]);

  useEffect(() => {
    if (!viewKey) return;
    const iv = setInterval(() => {
      setQuestionTimes((prev) => {
        const { si, qi } = viewKey;
        const sec = prev[si] ? { ...prev[si] } : {};
        sec[qi] = (sec[qi] || 0) + 1;
        return { ...prev, [si]: sec };
      });
    }, 1000);
    return () => clearInterval(iv);
  }, [viewKey]);

  const beginExam = () => {
    localStorage.removeItem(`attempt:${id}`);
    setAttemptRef(null);
    const now = new Date().toISOString();
    setStartedAt(now);

    // منطق جدید برای تایمر سراسری
    if (exam?.timeMode === "global") {
      setExamTimer((prev) => ({ ...prev, running: true }));
    }

    setPageIdx((i) => i + 1);
  };
  const beginSection = () => {
    const sec = exam.sections[curr.si];

    // تغییر این قسمت:
    if (
      exam?.timeMode !== "global" &&
      typeof sec.timeLimitSec === "number" &&
      sec.timeLimitSec > 0
    ) {
      setTimeLeft(sec.timeLimitSec);
      setTimerActive(true);
    } else {
      setTimeLeft(null);
      setTimerActive(false);
    }

    setPageIdx((i) => i + 1);
    setPlayArmed(true);
  };

  const headerColor = exam?.meta?.headerColor || "#0f766e";
  const isOnSection = curr?.si != null;
  const section = isOnSection ? exam?.sections?.[curr.si] : null;
  const sec = exam?.sections?.[curr?.si] || null;
  const audio = sec?.audioFile;
  const audioSrc = audio?.url || "";
  const allowPause = !!audio?.allowPause;
  const oneShot = audio?.maxPlays === 1;
  const layout = section?.meta?.ui?.layout || "single";
  const imageSize =
    mode === "device"
      ? exam?.meta?.imageSize
        ? exam?.meta?.imageSize
        : "100%"
      : "100%";

  return (
    <div style={{ width: imageSize }} className=" bg-white pb-16 mx-auto">
      {/* هدر: ردیف اول */}
      <div className="w-full" style={{ backgroundColor: headerColor }}>
        <div className="px-4 py-3 flex items-center justify-between text-white">
          <div className="flex items-center gap-2">
            <button
              onClick={async () => {
                await restoreLang();
                navigate(-1);
              }}
              className="inline-flex items-center gap-1 bg-white/10 hover:bg-white/20 px-3 py-1 rounded"
            >
              <X className="w-4 h-4" /> {t("preview.exit")}
            </button>
            <button
              onClick={() => setReviewOpen(true)}
              className="inline-flex items-center gap-1 bg-white/10 hover:bg-white/20 px-3 py-1 rounded"
            >
              <Bookmark className="w-4 h-4" /> {t("preview.review")}
            </button>
          </div>

          <div className="text-sm opacity-90 truncate">
            {exam?.title?.fa || exam?.titleFa || t("preview.exam")}
          </div>

          <div className="flex items-center gap-3">
            {/* ⏱️ اولویت با تایمر سکشن */}
            {exam?.timeMode === "global" ? (
              <div
                className={`text-lg font-mono px-3 py-1 rounded flex items-center gap-2 ${examTimer.leftSec < 60 ? "bg-red-600 animate-pulse" : "bg-black/20"}`}
              >
                <Clock className="w-5 h-5" />
                {formatMMSS(examTimer.leftSec)}
              </div>
            ) : (
              <>
                {/* نمایش تایمر سکشن - همان کد قبلی شما */}
                {hasLegacySectionTimer ? (
                  <div className="text-lg font-mono bg-black/20 px-3 py-1 rounded flex items-center gap-2">
                    <Clock className="w-5 h-5" />
                    {formatMMSS(timeLeft)}
                  </div>
                ) : hasExamTimer ? (
                  <div className="text-lg font-mono bg-black/20 px-3 py-1 rounded flex items-center gap-2">
                    <Clock className="w-5 h-5" />
                    {formatMMSS(examTimer.leftSec ?? examTimer.totalSec)}
                  </div>
                ) : null}
              </>
            )}

            {localPos >= 0 && (
              <div className="flex items-center gap-2">
                <button
                  onClick={goPrevInSection}
                  disabled={!canPrevInSection}
                  className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border ${
                    canPrevInSection
                      ? "hover:bg-white/10"
                      : "opacity-50 cursor-not-allowed"
                  }`}
                  title="Previous question"
                >
                  <ChevronLeft className="w-4 h-4" />
                  {t("preview.back")}
                </button>

                <button
                  onClick={goNextInSection}
                  disabled={!canNextInSection}
                  className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border ${
                    canNextInSection
                      ? "hover:bg白/10"
                      : "opacity-50 cursor-not-allowed"
                  }`}
                  title="Next question"
                >
                  {t("preview.next")}
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            )}

            {isPractice && exam?.sections?.length > 1 && (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    const prevSectionIdx = Math.max((curr?.si || 0) - 1, 0);
                    const prevIntroIdx = pages.findIndex(
                      (p) =>
                        p.type === "sectionIntro" && p.si === prevSectionIdx,
                    );
                    if (prevIntroIdx >= 0) setPageIdx(prevIntroIdx);
                  }}
                  disabled={(curr?.si || 0) <= 0}
                  className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border ${
                    (curr?.si || 0) <= 0
                      ? "opacity-50 cursor-not-allowed"
                      : "hover:bg-white/10"
                  }`}
                >
                  <ChevronRight className="w-4 h-4 ltr:rotate-180" />
                  {t("preview.prev_section") || "سکشن قبلی"}
                </button>

                <button
                  onClick={() => {
                    const nextSectionIdx = Math.min(
                      (curr?.si || 0) + 1,
                      exam.sections.length - 1,
                    );
                    const nextIntroIdx = pages.findIndex(
                      (p) =>
                        p.type === "sectionIntro" && p.si === nextSectionIdx,
                    );
                    if (nextIntroIdx >= 0) setPageIdx(nextIntroIdx);
                  }}
                  disabled={(curr?.si || 0) >= exam.sections.length - 1}
                  className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border ${
                    (curr?.si || 0) >= exam.sections.length - 1
                      ? "opacity-50 cursor-not-allowed"
                      : "hover:bg-white/10"
                  }`}
                >
                  {t("preview.next_section") || "سکشن بعدی"}
                  <ChevronLeft className="w-4 h-4 ltr:rotate-180" />
                </button>
              </div>
            )}

            {curr?.si != null &&
              curr?.type !== "sectionIntro" &&
              curr?.type !== "aiSectionRun" && (
                <button
                  onClick={finishSection}
                  className="bg-rose-600 hover:bg-rose-700 text-white px-3 py-1.5 rounded-lg text-sm"
                >
                  {t("preview.finish_section") || "Finish Section"}
                </button>
              )}
          </div>
          {/* فقط وقتی داخل سکشن هستیم و روی صفحهٔ سوال/پارت هستیم (نه sectionIntro) */}
        </div>

        {/* هدر: ردیف دوم = پلیر سفارشی (در صورت وجود) */}
        {audioSrc ? (
          <div className="border-t bg-white">
            <div className={` px-4 py-3`}>
              <AudioPlayer
                src={audioSrc}
                allowPause={allowPause}
                oneShot={oneShot}
                autoPlaySignal={autoPlaySignal}
              />
            </div>
          </div>
        ) : null}
      </div>

      {/* بدنه */}
      <div className="p-4">
        {loading ? (
          <div className="rounded-2xl border bg-white p-6 text-sm text-gray-600">
            {t("preview.loading")}
          </div>
        ) : !exam ? (
          <div className="rounded-2xl border bg-white p-6 text-sm text-red-600">
            {t("preview.not_found")}
          </div>
        ) : !curr ? (
          <div className="rounded-2xl border bg-white p-6 text-sm text-gray-600">
            {t("preview.no_pages")}
          </div>
        ) : curr.type === "examIntro" ? (
          <div className="rounded-2xl border bg-white p-6">
            <h1 className="text-2xl font-bold mb-2">
              {exam?.title?.fa || exam?.titleFa}
            </h1>
            {exam?.description?.fa && (
              <div
                className="text-gray-700 mb-4 prose  "
                dangerouslySetInnerHTML={{ __html: exam.description.fa }}
              />
            )}
            <div className="text-sm text-gray-600 mb-4">
              {t("preview.questions_n", { n: totalQuestions })}
            </div>
            <button
              onClick={beginExam}
              className="bg-emerald-600 text-white px-4 py-2 rounded-lg hover:bg-emerald-700"
            >
              {t("preview.begin_exam")}
            </button>
          </div>
        ) : curr.type === "sectionIntro" ? (
          <div className="rounded-2xl border bg-white p-6">
            <div className="text-xs text-gray-500 mb-1">
              {t("preview.section_n", { n: curr.si + 1 })}
            </div>
            <h2 className="text-xl font-semibold mb-2">
              {exam.sections[curr.si]?.title?.fa || t("preview.section")}
            </h2>
            {exam.sections[curr.si]?.description?.fa && (
              <div
                className="text-gray-700 mb-4 prose  "
                dangerouslySetInnerHTML={{
                  __html: exam.sections[curr.si].description.fa,
                }}
              />
            )}
            <button
              onClick={beginSection}
              className="bg-emerald-600 text-white px-4 py-2 rounded-lg hover:bg-emerald-700"
            >
              {t("preview.begin_section")}
            </button>
          </div>
        ) : curr.type === "aiSectionRun" ? (
          <AiSectionRunner
            examId={exam._id}
            section={exam.sections[curr.si]}
            sectionIndex={curr.si}
            attemptRef={attemptRef}
            token={JSON.parse(localStorage.getItem("user") || "{}")?.token}
            onDoneSection={(aiResult) => finishAiSection(curr.si, aiResult)}
          />
        ) : curr.type === "partAllAtOnce" ? (
          // ✅ کل پارت در یک صفحه: فقط promptHtml بالا؛ آیتم‌ها بدون stem
          (() => {
            const sec = exam.sections[curr.si];
            const part = findPartById(sec, curr.pid);
            const promptHtml = part?.promptHtml || "";
            const passageHtml = part?.passageHtml || "";
            const { parts, mode } = buildSectionPartNav(curr.si);
            const activeIdx = getActivePartIndex(curr.si);

            // گروه‌بندی
            const { sections: grouped, publicBlocks } = buildGroupedPart(
              part,
              sec,
              curr.qis || [],
            );

            return (
              <div className="rounded-2xl border bg-white p-5">
                <PartNav
                  parts={parts}
                  activePartIndex={activeIdx}
                  displayMode={mode}
                  onGoPart={(idx) => goToPartIndex(curr.si, idx)}
                  onGoQuestion={(qi) => {
                    const target = pages.findIndex(
                      (pg) =>
                        pg.si === curr.si &&
                        pg.type === "partOnePerPage" &&
                        pg.qi === qi,
                    );
                    if (target >= 0) setPageIdx(target);
                  }}
                />

                {layout === "two_pane" ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* ستون چپ: Passage + بلاک‌های عمومی فقط یک‌بار */}
                    <div className="prose max-w-none md:sticky md:top-4 md:max-h-[calc(100vh-220px)] md:overflow-y-auto">
                      {passageHtml ? (
                        <div
                          className=" "
                          dangerouslySetInnerHTML={{ __html: passageHtml }}
                        />
                      ) : null}

                      {publicBlocks.length ? (
                        <div className="mt-4 space-y-2">
                          {publicBlocks.map((b, bi) => (
                            <div
                              key={b?.id || bi}
                              className="rounded-md border bg-amber-50/40 p-3"
                            >
                              {b?.html ? (
                                <div
                                  className="prose prose-sm max-w-none  "
                                  dangerouslySetInnerHTML={{ __html: b.html }}
                                />
                              ) : null}
                            </div>
                          ))}
                        </div>
                      ) : null}
                    </div>

                    {/* ستون راست: گروه‌ها */}
                    <div className="space-y-6 md:sticky md:top-4 md:max-h-[calc(100vh-220px)] md:overflow-y-auto">
                      {promptHtml ? (
                        <div
                          className="prose prose-sm max-w-none  "
                          dangerouslySetInnerHTML={{ __html: promptHtml }}
                        />
                      ) : null}

                      {grouped.map((g, idx) =>
                        g.kind === "block" ? (
                          <div key={`blk-${idx}`} className="pt-4">
                            {/* هدر بلاک */}
                            <div className="rounded-md  border-b p-3 mb-3">
                              {g.block?.html ? (
                                <div
                                  className="prose prose-sm max-w-none  "
                                  dangerouslySetInnerHTML={{
                                    __html: g.block.html,
                                  }}
                                />
                              ) : null}
                            </div>

                            {/* سوال‌های داخل این بلاک */}
                            <div className="space-y-4">
                              {g.qis.map((qi, index) => (
                                <ItemRenderer
                                  key={qi}
                                  item={sec.items[qi]?.item}
                                  hideStem={true}
                                  onAnswerChange={(map) =>
                                    setItemAnswers(curr.si, qi, map)
                                  }
                                  initialAnswers={answers[curr.si]?.[qi]}
                                  questionNumber={qi + 1}
                                  practiceMode={isPractice}
                                  onExplainAi={() =>
                                    openAiHelp({
                                      item: sec.items[qi]?.item,
                                      si: curr.si,
                                      qi,
                                    })
                                  }
                                  canUseAi={canUseAi}
                                />
                              ))}
                            </div>
                          </div>
                        ) : (
                          <>
                            <ItemRenderer
                              key={`q-${g.qi}`}
                              item={sec.items[g.qi]?.item}
                              hideStem={true}
                              initialAnswers={answers[curr.si]?.[g.qi]}
                              onAnswerChange={(map) =>
                                setItemAnswers(curr.si, g.qi, map)
                              }
                              questionNumber={g.qi + 1}
                              practiceMode={isPractice}
                              onExplainAi={() =>
                                openAiHelp({
                                  item: sec.items[g.qi]?.item,
                                  si: curr.si,
                                  qi: g.qi,
                                })
                              }
                              canUseAi={canUseAi}
                            />
                          </>
                        ),
                      )}
                    </div>
                  </div>
                ) : (
                  <>
                    {/* تک‌ستونه */}
                    {promptHtml ? (
                      <div
                        className="prose prose-sm max-w-none mb-3  "
                        dangerouslySetInnerHTML={{ __html: promptHtml }}
                      />
                    ) : null}

                    {/* بلاک‌های عمومی فقط یک‌بار بالا */}
                    {publicBlocks.length ? (
                      <div className="mb-4 space-y-2">
                        {publicBlocks.map((b, bi) => (
                          <div
                            key={b?.id || bi}
                            className="rounded-md border bg-amber-50/40 p-3"
                          >
                            {b?.html ? (
                              <div
                                className="prose prose-sm max-w-none  "
                                dangerouslySetInnerHTML={{ __html: b.html }}
                              />
                            ) : null}
                          </div>
                        ))}
                      </div>
                    ) : null}

                    {/* گروه‌ها */}
                    <div className="">
                      {grouped.map((g, idx) =>
                        g.kind === "block" ? (
                          <div key={`blk-${idx}`} className="py-4 border-t">
                            <div className=" mb-3">
                              {g.block?.html ? (
                                <div
                                  className="prose prose-sm max-w-none  "
                                  dangerouslySetInnerHTML={{
                                    __html: g.block.html,
                                  }}
                                />
                              ) : null}
                            </div>
                            <div className="space-y-4">
                              {g.qis.map((qi) => (
                                <ItemRenderer
                                  key={qi}
                                  index={idx}
                                  item={sec.items[qi]?.item}
                                  hideStem={true}
                                  onAnswerChange={(map) =>
                                    setItemAnswers(curr.si, qi, map)
                                  }
                                  initialAnswers={answers[curr.si]?.[qi]}
                                  questionNumber={qi + 1}
                                  practiceMode={isPractice}
                                  onExplainAi={() =>
                                    openAiHelp({
                                      item: sec.items[qi]?.item,
                                      si: curr.si,
                                      qi,
                                    })
                                  }
                                  canUseAi={canUseAi}
                                />
                              ))}
                            </div>
                          </div>
                        ) : (
                          <ItemRenderer
                            key={`q-${g.qi}`}
                            index={idx}
                            item={sec.items[g.qi]?.item}
                            hideStem={true}
                            onAnswerChange={(map) =>
                              setItemAnswers(curr.si, g.qi, map)
                            }
                            initialAnswers={answers[curr.si]?.[g.qi]}
                            questionNumber={g.qi + 1}
                            practiceMode={isPractice}
                            onExplainAi={() =>
                              openAiHelp({
                                item: sec.items[g.qi]?.item,
                                si: curr.si,
                                qi: g.qi,
                              })
                            }
                            canUseAi={canUseAi}
                          />
                        ),
                      )}
                    </div>
                  </>
                )}
              </div>
            );
          })()
        ) : curr.type === "partOnePerPage" ? (
          // ✅ هر صفحه یک آیتم: promptHtml بالا؛ stem آیتم نمایش داده بشه
          (() => {
            // ... داخل IIFE partOnePerPage ...
            const sec = exam.sections[curr.si];
            const part = findPartById(sec, curr.pid);
            const promptHtml = part?.promptHtml || "";
            const passageHtml = part?.passageHtml || "";
            const { parts, mode } = buildSectionPartNav(curr.si);
            const activeIdx = getActivePartIndex(curr.si);

            const qItem = sec.items[curr.qi];
            const q1 = (curr.qi ?? 0) + 1;

            // بلاکی که این سؤال داخلِ بازه‌ی آن است
            const blocks = Array.isArray(part?.blocks) ? part.blocks : [];
            const hostingBlock = blocks.find((b) => inRange(b, q1));

            // فقط اگر «اولین سؤال» آن بلاک هستیم، هدر را نشان بده:
            const showBlockHeader =
              hostingBlock &&
              (hostingBlock.from === q1 || hostingBlock.from == null);

            const row = sec.items[curr.qi];
            const isGate = row?.item?.kind === "audio_gate";
            const qNum = !isGate
              ? getQuestionNumber(curr.si, curr.qi)
              : undefined;

            const isBookmarked = bookmarks.has(pageIdx);
            const toggleBookmark = () => {
              setBookmarks((prev) => {
                const next = new Set(prev);
                if (next.has(pageIdx)) next.delete(pageIdx);
                else next.add(pageIdx);
                return next;
              });
            };

            return (
              <div className="rounded-2xl border bg-white p-5">
                {/* <PartNav
                  parts={parts}
                  activePartIndex={activeIdx}
                  displayMode={mode}
                  onGoPart={(idx) => goToPartIndex(curr.si, idx)}
                  onGoQuestion={(qi) => {
                    const target = pages.findIndex(
                      (pg) =>
                        pg.si === curr.si &&
                        pg.type === "partOnePerPage" &&
                        pg.qi === qi
                    );
                    if (target >= 0) setPageIdx(target);
                  }}
                  activeQuestion1Based={q1}
                /> */}

                {layout === "two_pane" ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="prose max-w-none md:sticky md:top-4 md:max-h-[calc(100vh-220px)] md:overflow-y-auto">
                      {passageHtml ? (
                        <div
                          className=" "
                          dangerouslySetInnerHTML={{ __html: passageHtml }}
                        />
                      ) : promptHtml ? (
                        <div
                          className=" "
                          dangerouslySetInnerHTML={{ __html: promptHtml }}
                        />
                      ) : null}

                      {/* اگر بلاک عمومی بود و این اولین سؤال بازه‌اش هم هست، اینجا هدر را نشان بده */}
                      {showBlockHeader &&
                      !hostingBlock?.from &&
                      !hostingBlock?.to ? (
                        <div className="mt-4 rounded-md border bg-amber-50/40 p-3">
                          {hostingBlock?.html ? (
                            <div
                              className="prose prose-sm max-w-none  "
                              dangerouslySetInnerHTML={{
                                __html: hostingBlock.html,
                              }}
                            />
                          ) : null}
                        </div>
                      ) : null}
                    </div>

                    <div>
                      {/* اگر بلاک رنج‌دار است و ما اولین سوال بازه‌ایم، هدر بالای سوال بیاید */}
                      {showBlockHeader &&
                      (hostingBlock?.from || hostingBlock?.to) ? (
                        <div className="pt-4 border-t mb-3">
                          <div className="rounded-md border bg-amber-50/40 p-3">
                            {hostingBlock?.html ? (
                              <div
                                className="prose prose-sm max-w-none  "
                                dangerouslySetInnerHTML={{
                                  __html: hostingBlock.html,
                                }}
                              />
                            ) : null}
                            <div className="text-[11px] text-gray-500 mt-1">
                              {t("preview.block_range", {
                                from: hostingBlock?.from || "",
                                to: hostingBlock?.to || "",
                              })}
                            </div>
                          </div>
                        </div>
                      ) : null}

                      <div className="flex items-center justify-between mb-2">
                        <div className="text-xs text-gray-500">
                          {t("preview.question")} {qNum ?? q1}
                        </div>
                        <button
                          onClick={toggleBookmark}
                          className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded border ${
                            isBookmarked
                              ? "bg-amber-100 text-amber-800"
                              : "hover:bg-gray-50"
                          }`}
                          title={
                            isBookmarked
                              ? t("preview.remove_bookmark")
                              : t("preview.add_bookmark")
                          }
                        >
                          <Bookmark
                            className={`w-4 h-4 ${
                              isBookmarked ? "fill-current" : ""
                            }`}
                          />
                          {isBookmarked
                            ? t("preview.bookmarked")
                            : t("preview.bookmark")}
                        </button>
                      </div>

                      <ItemRenderer
                        item={row?.item}
                        hideStem={false}
                        onAnswerChange={(map) =>
                          setItemAnswers(curr.si, curr.qi, map)
                        }
                        initialAnswers={answers[curr.si]?.[curr.qi]}
                        questionNumber={qNum}
                        onContinue={() =>
                          setPageIdx((i) => Math.min(pages.length - 1, i + 1))
                        }
                        practiceMode={isPractice}
                        onExplainAi={() =>
                          openAiHelp({
                            item: row?.item,
                            si: curr.si,
                            qi: curr.qi,
                          })
                        }
                        canUseAi={canUseAi}
                      />
                    </div>
                  </div>
                ) : (
                  <>
                    {promptHtml ? (
                      <div
                        className="prose prose-sm max-w-none mb-4  "
                        dangerouslySetInnerHTML={{ __html: promptHtml }}
                      />
                    ) : null}

                    {/* تک‌ستونه: هدر بلاک فقط روی اولین سؤال بازه */}
                    {showBlockHeader && (
                      <div className="pt-4 border-t mb-3">
                        <div className="rounded-md border bg-amber-50/40 p-3">
                          {hostingBlock?.html ? (
                            <div
                              className="prose prose-sm max-w-none  "
                              dangerouslySetInnerHTML={{
                                __html: hostingBlock.html,
                              }}
                            />
                          ) : null}
                          {(hostingBlock?.from || hostingBlock?.to) && (
                            <div className="text-[11px] text-gray-500 mt-1">
                              {t("preview.block_range", {
                                from: hostingBlock?.from || "",
                                to: hostingBlock?.to || "",
                              })}
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    <ItemRenderer
                      item={row?.item}
                      hideStem={false}
                      onAnswerChange={(map) =>
                        setItemAnswers(curr.si, curr.qi, map)
                      }
                      initialAnswers={answers[curr.si]?.[curr.qi]}
                      questionNumber={qNum}
                      onContinue={() =>
                        setPageIdx((i) => Math.min(pages.length - 1, i + 1))
                      }
                      practiceMode={isPractice}
                      onExplainAi={() =>
                        openAiHelp({
                          item: sec.items[curr?.qi]?.item,
                          si: curr.si,
                          qi: curr?.qi,
                        })
                      }
                      canUseAi={canUseAi}
                    />
                  </>
                )}
              </div>
            );
          })()
        ) : (
          // fallback قدیمی (در صورت باقی‌ماندن صفحات نوع دیگر)
          <div>
            <div className="flex items-center justify-between mb-2 text-xs text-gray-600">
              <span>
                {t("preview.question_of", { a: pageIdx + 1, b: pages.length })}
              </span>
              <span>{t("preview.section_n", { n: (curr.si || 0) + 1 })}</span>
            </div>
            <ItemRenderer
              item={exam.sections[curr.si]?.items?.[curr.qi]?.item}
              onAnswerChange={(map) => setItemAnswers(curr.si, curr.qi, map)}
              questionNumber={(curr.qi ?? 0) + 1}
              practiceMode={isPractice}
              onExplainAi={() =>
                openAiHelp({
                  item: sec.items[curr.qi]?.item,
                  si: curr.si,
                  qi: curr.qi,
                })
              }
              canUseAi={canUseAi}
            />
          </div>
        )}
      </div>
      <ReviewModal
        open={reviewOpen}
        onClose={() => setReviewOpen(false)}
        sections={exam?.sections || []}
        answers={answers}
        pages={pages}
        goToPage={(idx) => setPageIdx(idx)}
      />
      {aiHelp.open && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="w-full max-w-3xl bg-white rounded-2xl shadow-lg border overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b">
              <div className="font-semibold">توضیح هوش مصنوعی</div>
              <button
                className="px-2 py-1 rounded hover:bg-gray-100"
                onClick={() => setAiHelp((p) => ({ ...p, open: false }))}
              >
                ✕
              </button>
            </div>

            <div className="p-4 max-h-[70vh] overflow-auto">
              {aiHelp.loading ? (
                <div className="text-sm text-gray-500">
                  در حال آماده‌سازی توضیح…
                </div>
              ) : aiHelp.error ? (
                <div className="text-sm text-rose-600">{aiHelp.error}</div>
              ) : (
                <div className="prose max-w-none">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {aiHelp.rendered || aiHelp.text}
                  </ReactMarkdown>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
